import { action, observable, runInAction, makeObservable } from 'mobx';
import { get, post, patch, remove } from '@partsbadger/utils';
import { IParts, IPartGroup } from '../types/types';
import { CoatingsProps, FinishesProps, MaterialProps } from '@partsbadger/types';

interface IAllGroupsAssignedToThePart {
    id: number;
    name: string;
}

class PartStore {
    loading = false;
    part: IParts | null = null;
    parts: IParts[] = [];

    materials: Array<MaterialProps> = [];
    finishes: Array<FinishesProps> = [];
    coatings: Array<CoatingsProps> = [];
    partsGroupList: IPartGroup[] = [];
    selectedlineItemsToGroup = 0;
    partsGroupDetails: IPartGroup | null = null;
    allGroupsAssignedToThePart: IAllGroupsAssignedToThePart[] = [];
    selectedTab = 'part';

    constructor() {
        makeObservable(this, {
            loading: observable,
            part: observable,
            parts: observable,
            materials: observable,
            finishes: observable,
            coatings: observable,
            partsGroupList: observable,
            partsGroupDetails: observable,
            allGroupsAssignedToThePart: observable,
            selectedTab: observable,

            // Actions
            setLoading: action,
            getAll: action,
            getMaterials: action,
            getFinishes: action,
            getCoatings: action,
            setSelectedlineItemsToGroup: action,
            getPartsGroupList: action,
            createPartsGroup: action,
            updatePartsGroup: action,
            deletePartsGroup: action,
            getPartsGroupById: action,
            getGroupListByPartId: action,
            setSelectedTab: action,
            getPartDetailsById: action,
        });
    }

    clearAllGroupsAssignedToThePart() {
        this.allGroupsAssignedToThePart = [];
    }

    setSelectedTab(tabKey: string) {
        this.selectedTab = tabKey;
    }

    setLoading(loading: boolean) {
        runInAction(() => {
            this.loading = loading;
        });
    }

    async getAll(params: any) {
        this.loading = true;
        const response = await get(`/customer/parts/`, params).finally(() => (this.loading = false));
        runInAction(() => {
            this.parts = response.data;
        });
    }

    async getMaterials(params?: any) {
        await get(`/customer/materials/`, {
            params: params,
        })
            .then(data => {
                runInAction(() => {
                    this.materials = data.results;
                });
            })
            .catch(error => {
                console.error(error);
            });
    }

    async getFinishes(params?: any) {
        await get(`/customer/finishes/`, {
            params: params,
        })
            .then(data => {
                runInAction(() => {
                    this.finishes = data.results;
                });
            })
            .catch(error => {
                console.error(error);
            });
    }

    async getCoatings(params?: any) {
        await get(`/customer/coatings/`, {
            params: params,
        })
            .then(data => {
                runInAction(() => {
                    this.coatings = data.results;
                });
            })
            .catch(error => {
                console.error(error);
            });
    }

    setSelectedlineItemsToGroup(lineItemId: number) {
        this.selectedlineItemsToGroup = lineItemId;
    }

    getPartsGroupList = async (params: any) => {
        return await get(`/customer/quote-line-items-group/`, { params: params }).then(resp => {
            this.partsGroupList = resp.results;

            return resp;
        });
    };

    createPartsGroup = async (name: string) => {
        const payload = {
            name: name,
        };

        const response = await post(`/customer/quote-line-items-group/`, payload);

        this.partsGroupList = [...this.partsGroupList, response];

        return response;
    };

    updatePartsGroup = async (groupId: number, payload: any) => {
        const response = await patch(`/customer/quote-line-items-group/${groupId}/update-line-item-group/`, payload);

        this.partsGroupDetails = response;

        if (this?.part) {
            this.getGroupListByPartId(this.part.id);
        }

        return response;
    };

    deletePartsGroup = async (groupId: number) => {
        const response = await remove(`/customer/quote-line-items-group/${groupId}/`);

        this.partsGroupList = this.partsGroupList.filter(item => {
            return item.id !== groupId;
        });

        return response;
    };

    getPartsGroupById = async (id: number) => {
        return await get(`/customer/quote-line-items-group/${id}/`).then(resp => {
            this.partsGroupDetails = resp;

            return resp;
        });
    };

    deletePartFromGroup = async (id: number, payload: any) => {
        return await post(`/customer/quote-line-items-group/${id}/delete-line-item-from-group/`, payload).then(resp => {
            this.partsGroupDetails = resp;

            return resp;
        });
    };

    getGroupListByPartId = async (master_product_id: number) => {
        const payload = {
            part_id: master_product_id,
        };

        return await post(`/customer/quote-line-items-group/all-groups-by-part/`, payload).then(resp => {
            this.allGroupsAssignedToThePart = resp;

            return resp;
        });
    };

    getPartDetailsById = async (id: number) => {
        return await get(`/customer/parts/${id}/`).then(resp => {
            this.part = resp;

            return resp;
        });
    };
}

const partStore = new PartStore();

export default partStore;
